import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  generateFromTemplate,
  getMyTemplateOverride,
  getPublishedTemplate,
  listUserLlmProviders,
  upsertMyTemplateOverride,
} from '@/services/templateSystemApi';
import { queryKeys } from '@/lib/queryKeys';
import { getUserFriendlyMessage } from '@/services/apiError';
import FormMessage from '@/components/common/FormMessage';
import PageLoading from '@/components/common/PageLoading';
import ContentApiErrorState, {
  ContentLoadError,
  ContentNotFound,
} from '@/components/common/ContentErrorState';
import TemplateDynamicForm from '@/components/templates/TemplateDynamicForm';

export default function TemplateRunPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [values, setValues] = useState<Record<string, unknown>>({});
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [selectedModelId, setSelectedModelId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [useCustomTemplate, setUseCustomTemplate] = useState(false);
  const [overrideDraft, setOverrideDraft] = useState({
    systemPromptOverride: '',
    planningPromptOverride: '',
    generationPromptOverride: '',
  });
  const [overrideId, setOverrideId] = useState<string | undefined>(undefined);
  const [overrideSaving, setOverrideSaving] = useState(false);
  const [overrideError, setOverrideError] = useState('');
  const [overrideSuccess, setOverrideSuccess] = useState('');
  const hasInitializedOverride = useRef(false);

  const templateQuery = useQuery({
    queryKey: queryKeys.templates.detail(id ?? ''),
    queryFn: () => getPublishedTemplate(id ?? ''),
    enabled: Boolean(id),
    retry: 1,
  });

  const providersQuery = useQuery({
    queryKey: queryKeys.llmProviders.list,
    queryFn: () => listUserLlmProviders(),
    retry: 1,
  });

  const overrideQuery = useQuery({
    queryKey: queryKeys.templateOverrides.my(id ?? ''),
    queryFn: () => getMyTemplateOverride(id ?? ''),
    enabled: Boolean(id),
    retry: 1,
  });

  useEffect(() => {
    const template = templateQuery.data;
    if (!template) {
      return;
    }

    setValues((prev) => {
      if (Object.keys(prev).length > 0) {
        return prev;
      }
      return deriveDefaultValues(template.inputSchemaJson);
    });
  }, [templateQuery.data]);

  useEffect(() => {
    const providers = providersQuery.data ?? [];
    if (providers.length === 0) {
      return;
    }

    const fallbackProvider = findDefaultProvider(providers);
    if (!fallbackProvider) {
      return;
    }

    if (!selectedProviderId) {
      setSelectedProviderId(fallbackProvider.id);
    }

    const resolvedProvider = providers.find((provider) => provider.id === (selectedProviderId || fallbackProvider.id)) ?? fallbackProvider;
    const hasSelectedModel = resolvedProvider.models.some((model) => model.id === selectedModelId);

    if (!selectedModelId || !hasSelectedModel) {
      const fallbackModel = findDefaultModel(resolvedProvider.models);
      setSelectedModelId(fallbackModel?.id ?? '');
    }
  }, [providersQuery.data, selectedModelId, selectedProviderId]);

  useEffect(() => {
    const override = overrideQuery.data?.override;
    if (!override || hasInitializedOverride.current) {
      return;
    }

    setOverrideId(override.id);
    setUseCustomTemplate(override.isActive);
    setOverrideDraft({
      systemPromptOverride: override.systemPromptOverride ?? '',
      planningPromptOverride: override.planningPromptOverride ?? '',
      generationPromptOverride: override.generationPromptOverride ?? '',
    });

    hasInitializedOverride.current = true;
  }, [overrideQuery.data?.override]);

  const selectedOutputTargets = useMemo(
    () => templateQuery.data?.outputTargetsJson ?? [],
    [templateQuery.data],
  );

  const selectedProvider = useMemo(() => {
    return (providersQuery.data ?? []).find((provider) => provider.id === selectedProviderId) ?? null;
  }, [providersQuery.data, selectedProviderId]);

  const selectedModel = useMemo(() => {
    if (!selectedProvider) {
      return null;
    }
    return selectedProvider.models.find((model) => model.id === selectedModelId) ?? null;
  }, [selectedProvider, selectedModelId]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!id) {
      return;
    }

    setError('');
    setSubmitting(true);

    try {
      const fallbackProvider = findDefaultProvider(providersQuery.data ?? []);
      const resolvedProvider = selectedProvider ?? fallbackProvider;
      const fallbackModel = findDefaultModel(resolvedProvider?.models ?? []);
      const resolvedModel = selectedModel ?? fallbackModel;

      const response = await generateFromTemplate(id, values, {
        providerId: resolvedProvider?.id,
        modelId: resolvedModel?.id,
        templateOverrideId: useCustomTemplate ? overrideId : undefined,
      });
      navigate(`/generation-jobs/${response.jobId}`);
    } catch (unknownError) {
      setError(getUserFriendlyMessage(unknownError, '生成失败，请检查输入字段'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveMyTemplate = async () => {
    if (!id) {
      return;
    }

    setOverrideError('');
    setOverrideSuccess('');
    setOverrideSaving(true);

    try {
      const saved = await upsertMyTemplateOverride(id, {
        systemPromptOverride: overrideDraft.systemPromptOverride,
        planningPromptOverride: overrideDraft.planningPromptOverride,
        generationPromptOverride: overrideDraft.generationPromptOverride,
        isActive: true,
      });
      setOverrideId(saved.id);
      setUseCustomTemplate(true);
      setOverrideSuccess('已保存你的自定义模板');
    } catch (unknownError) {
      setOverrideError(getUserFriendlyMessage(unknownError, '保存自定义模板失败'));
    } finally {
      setOverrideSaving(false);
    }
  };

  if (templateQuery.isLoading) {
    return <PageLoading message='模板加载中…' />;
  }

  if (!id) {
    return (
      <div className='max-w-[980px] mx-auto px-6 py-12'>
        <ContentNotFound />
      </div>
    );
  }

  if (templateQuery.isError) {
    return (
      <div className='max-w-[980px] mx-auto px-6 py-12'>
        <ContentApiErrorState error={templateQuery.error} onRetry={() => void templateQuery.refetch()} />
      </div>
    );
  }

  if (!templateQuery.data) {
    return (
      <div className='max-w-[980px] mx-auto px-6 py-12'>
        <ContentLoadError onRetry={() => void templateQuery.refetch()} />
      </div>
    );
  }

  return (
    <div className='max-w-[980px] mx-auto px-6 py-12'>
      <div className='flex items-center justify-between gap-3 mb-6'>
        <Link to='/templates' className='btn-ghost'>
          ‹ 返回模板库
        </Link>
      </div>

      <h1
        className='font-semibold text-apple-near-black mb-2'
        style={{ fontSize: '2rem', lineHeight: 1.1, letterSpacing: '-0.02em' }}
      >
        {templateQuery.data.name}
      </h1>
      <p className='text-apple-gray mb-6 text-[0.9375rem]'>{templateQuery.data.description}</p>

      {error ? (
        <div className='mb-4'>
          <FormMessage tone='error' message={error} />
        </div>
      ) : null}

      {providersQuery.isError ? (
        <div className='mb-4'>
          <ContentApiErrorState error={providersQuery.error} onRetry={() => void providersQuery.refetch()} />
        </div>
      ) : null}

      <form className='bg-white rounded-2xl p-6 shadow-card-sm space-y-6' onSubmit={handleSubmit}>
        <h2 className='font-semibold text-[1.125rem] text-apple-near-black'>填写输入参数</h2>

        <TemplateDynamicForm
          schema={templateQuery.data.inputSchemaJson}
          uiSchema={templateQuery.data.uiSchemaJson}
          values={values}
          onChange={setValues}
          disabled={submitting}
        />

        <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
          <div className='space-y-1.5'>
            <label htmlFor='template-run-provider' className='block text-[0.875rem] font-medium text-apple-near-black'>
              LLM Provider
            </label>
            <select
              id='template-run-provider'
              className='input'
              value={selectedProviderId}
              onChange={(event) => {
                setSelectedProviderId(event.target.value);
                const provider = (providersQuery.data ?? []).find((item) => item.id === event.target.value);
                const fallbackModel = findDefaultModel(provider?.models ?? []);
                setSelectedModelId(fallbackModel?.id ?? '');
              }}
              disabled={providersQuery.isLoading || submitting}
            >
              {(providersQuery.data ?? []).map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.name}
                </option>
              ))}
            </select>
          </div>

          <div className='space-y-1.5'>
            <label htmlFor='template-run-model' className='block text-[0.875rem] font-medium text-apple-near-black'>
              Model
            </label>
            <select
              id='template-run-model'
              className='input'
              value={selectedModelId}
              onChange={(event) => setSelectedModelId(event.target.value)}
              disabled={!selectedProvider || submitting}
            >
              {(selectedProvider?.models ?? []).map((model) => (
                <option key={model.id} value={model.id}>
                  {model.label || model.modelId}
                </option>
              ))}
            </select>
          </div>
        </div>

        <section className='border border-apple-border rounded-xl p-4 space-y-3'>
          <div className='flex items-center justify-between gap-3'>
            <h3 className='font-medium text-apple-near-black'>Prompt Template</h3>
            <button
              type='button'
              className='btn btn-secondary'
              onClick={() => {
                setUseCustomTemplate(true);
                setOverrideSuccess('');
                setOverrideError('');
              }}
            >
              使用我的自定义模板
            </button>
          </div>

          {overrideQuery.isError ? (
            <ContentApiErrorState error={overrideQuery.error} onRetry={() => void overrideQuery.refetch()} />
          ) : null}

          {useCustomTemplate ? (
            <div className='space-y-3'>
              <div className='space-y-1.5'>
                <label htmlFor='prompt-system-override' className='block text-[0.8125rem] font-medium text-apple-near-black'>
                  System Prompt Override
                </label>
                <textarea
                  id='prompt-system-override'
                  className='input min-h-24'
                  value={overrideDraft.systemPromptOverride}
                  onChange={(event) =>
                    setOverrideDraft((prev) => ({
                      ...prev,
                      systemPromptOverride: event.target.value,
                    }))
                  }
                />
              </div>

              <div className='space-y-1.5'>
                <label htmlFor='prompt-planning-override' className='block text-[0.8125rem] font-medium text-apple-near-black'>
                  Planning Prompt Override
                </label>
                <textarea
                  id='prompt-planning-override'
                  className='input min-h-24'
                  value={overrideDraft.planningPromptOverride}
                  onChange={(event) =>
                    setOverrideDraft((prev) => ({
                      ...prev,
                      planningPromptOverride: event.target.value,
                    }))
                  }
                />
              </div>

              <div className='space-y-1.5'>
                <label htmlFor='prompt-generation-override' className='block text-[0.8125rem] font-medium text-apple-near-black'>
                  Generation Prompt Override
                </label>
                <textarea
                  id='prompt-generation-override'
                  className='input min-h-24'
                  value={overrideDraft.generationPromptOverride}
                  onChange={(event) =>
                    setOverrideDraft((prev) => ({
                      ...prev,
                      generationPromptOverride: event.target.value,
                    }))
                  }
                />
              </div>

              {overrideError ? <FormMessage tone='error' message={overrideError} /> : null}
              {overrideSuccess ? <FormMessage tone='success' message={overrideSuccess} /> : null}

              <button
                type='button'
                className='btn btn-secondary'
                disabled={overrideSaving}
                onClick={() => void handleSaveMyTemplate()}
              >
                {overrideSaving ? '保存中…' : '保存我的自定义模板'}
              </button>
            </div>
          ) : (
            <p className='text-[0.8125rem] text-apple-gray'>当前使用系统默认模板，可切换为你的自定义模板。</p>
          )}
        </section>

        <div>
          <p className='text-[0.875rem] font-medium text-apple-near-black mb-2'>将生成的输出</p>
          <div className='flex flex-wrap gap-2'>
            {selectedOutputTargets.map((target) => (
              <span key={target} className='text-[0.75rem] px-2 py-1 rounded-full bg-apple-light-gray text-apple-gray'>
                {target}
              </span>
            ))}
          </div>
        </div>

        <button type='submit' className='btn btn-primary' disabled={submitting}>
          {submitting ? '生成中…' : 'Generate'}
        </button>
      </form>
    </div>
  );
}

function deriveDefaultValues(schema: Record<string, unknown>) {
  if (!schema || schema.type !== 'object' || typeof schema.properties !== 'object' || schema.properties == null) {
    return {};
  }

  const properties = schema.properties as Record<string, { default?: unknown }>;
  const values: Record<string, unknown> = {};

  for (const [key, property] of Object.entries(properties)) {
    if (property.default !== undefined) {
      values[key] = property.default;
    }
  }

  return values;
}

function findDefaultProvider<T extends { id: string; isDefault?: boolean; enabled?: boolean }>(providers: T[]) {
  const enabledProviders = providers.filter((provider) => provider.enabled !== false);
  if (enabledProviders.length === 0) {
    return null;
  }

  return enabledProviders.find((provider) => provider.isDefault) ?? enabledProviders[0];
}

function findDefaultModel<T extends { id: string; isDefault?: boolean; isEnabled?: boolean }>(models: T[]) {
  const enabledModels = models.filter((model) => model.isEnabled !== false);
  if (enabledModels.length === 0) {
    return null;
  }

  return enabledModels.find((model) => model.isDefault) ?? enabledModels[0];
}
